/**
 * Reflights and patches (MB, decision 29 / Q92, 18 Oct 2026; spec overview.md "Reflights and patches").
 *
 * - Rejecting a line creates its **reflight** at once: the next version of the line number (100070 → 100071) with
 *   the original's geometry and length, `reflight_of_line_id` = the original line.
 * - Part-accepting a line creates a **patch**: the next version, covering only the rejected segment of the original
 *   line. Its end points are the segment's ends on the original line, `length_m` is the segment length, and
 *   `segment_from_m` / `segment_to_m` give the segment in metres along the original from its first point (x1, y1).
 *   `reflight_of_line_id` is always the original (version 0) line, also for a reflight of a reflight or a patch of
 *   a patch.
 * - Neither ever adds to the planned km (see progress.ts): only original lines count.
 *
 * The default patch segment is the part of the planned line that accepted data does not cover. The accepted data
 * is taken as the first `accepted_m` metres of the logged run, from where the log says the line started (projected
 * onto the planned line), in the direction flown. Without logged coordinates the run is assumed to start at the end
 * of the line the heading points away from (or at the line's first point when the heading is unknown). When the
 * accepted data covers the whole line, the patch comes from the line's bad sections by the Suggested patches rule
 * (`partialPatch` in patch-suggestions.ts, issue #69); `followUpPlan` is always given that segment.
 */
import { parseLineNumber } from './lines.js';
import type { Acceptance, Block, IsoDateTime, LineStatus, PlannedLine, Uuid } from './model.js';
import type { LineExtent } from './zdas.js';

export type PlannedLineRole = 'original' | 'reflight' | 'patch';

/** A logged point further than this from the planned line (metres) is not projected onto it. */
export const MAX_OFFSET_FROM_LINE_M = 500;
/**
 * Uncovered pieces shorter than this (metres) are ignored when choosing the default patch segment, as long as a
 * longer piece exists (a run-in that started a few metres late is not a gap to refly).
 */
export const PATCH_MIN_GAP_M = 50;

export function isPatch(line: PlannedLine): boolean {
  return line.segment_from_m != null && line.segment_to_m != null;
}

/** Brought into the plan by an imported lines file (R245; `lines-import.ts`). */
export function isImportedLine(line: Pick<PlannedLine, 'lines_file_id'>): boolean {
  return line.lines_file_id != null;
}

/** A new line of the plan, brought in by an import (R245): an original line with an import's id. */
export function isInfillLine(line: Pick<PlannedLine, 'lines_file_id' | 'reflight_of_line_id'>): boolean {
  return isImportedLine(line) && line.reflight_of_line_id === null;
}

type InfillLine = Pick<
  PlannedLine,
  'block_id' | 'length_m' | 'lines_file_id' | 'reflight_of_line_id' | 'deleted_at'
>;

/**
 * A block's planned km (decision 1052): the flight plan's `planned_km` plus its live infill lines. Worked out from the
 * lines and never stored, so a take-back or a sync changes it by itself, two laptops cannot drift apart, and the roles that
 * import need not write the block. Infill flown as a new block has its own block number and planned km.
 */
export function blockPlannedKm(block: Pick<Block, 'id' | 'planned_km'>, plan: readonly InfillLine[]): number {
  const infillM = plan
    .filter((p) => p.block_id === block.id && p.deleted_at === null && isInfillLine(p))
    .reduce((t, p) => t + p.length_m, 0);
  return block.planned_km + infillM / 1000;
}

/** Blocks with their planned km as `blockPlannedKm` gives it, for what reads `planned_km` from a block. */
export function withInfillKm<T extends Pick<Block, 'id' | 'planned_km'>>(
  blocks: readonly T[],
  plan: readonly InfillLine[],
): T[] {
  return blocks.map((b) => ({ ...b, planned_km: blockPlannedKm(b, plan) }));
}

/** Original line, full reflight (whole line again) or patch (part of the line again). */
export function plannedLineRole(line: PlannedLine): PlannedLineRole {
  if (isPatch(line)) return 'patch';
  return line.reflight_of_line_id === null ? 'original' : 'reflight';
}

const cm = (m: number): number => Math.round(m * 100) / 100;

function lineLength(line: PlannedLine): number {
  return Math.hypot(line.x2 - line.x1, line.y2 - line.y1);
}

/** The point `d` metres along `line` from (x1, y1). */
export function pointAlong(line: PlannedLine, d: number): readonly [number, number] {
  const len = lineLength(line);
  if (len === 0) return [line.x1, line.y1];
  const t = d / len;
  return [line.x1 + (line.x2 - line.x1) * t, line.y1 + (line.y2 - line.y1) * t];
}

/**
 * Metres along `line` from (x1, y1) of the foot of `p` on the line (may be < 0 or > length when beyond an end), or
 * null when the line has no geometry or `p` is more than `MAX_OFFSET_FROM_LINE_M` off it.
 */
export function projectAlong(line: PlannedLine, p: readonly [number, number]): number | null {
  const len = lineLength(line);
  if (len === 0) return null;
  const ux = (line.x2 - line.x1) / len;
  const uy = (line.y2 - line.y1) / len;
  const dx = p[0] - line.x1;
  const dy = p[1] - line.y1;
  const along = dx * ux + dy * uy;
  const offset = Math.abs(dx * uy - dy * ux);
  return offset > MAX_OFFSET_FROM_LINE_M ? null : cm(along);
}

/** `start_along_m` / `end_along_m` of a logged line on its original planned line (both null when unusable). */
export function loggedAlong(
  extent: LineExtent | undefined,
  original: PlannedLine | undefined,
): { readonly start_along_m: number | null; readonly end_along_m: number | null } {
  if (!extent || !original) return { start_along_m: null, end_along_m: null };
  const s = projectAlong(original, extent.start);
  const e = projectAlong(original, extent.end);
  return s === null || e === null
    ? { start_along_m: null, end_along_m: null }
    : { start_along_m: s, end_along_m: e };
}

export interface Segment {
  /** Metres along the original line from its first point. */
  readonly from_m: number;
  readonly to_m: number;
}

/** Heading of `line` from (x1, y1) to (x2, y2), degrees clockwise from grid north. */
function bearing(line: PlannedLine): number {
  return ((Math.atan2(line.x2 - line.x1, line.y2 - line.y1) * 180) / Math.PI + 360) % 360;
}

/**
 * The default patch segment for a part-accepted status of `original`: the smallest single segment that holds every
 * part of the line not covered by the accepted data (see the module note), or null when the accepted data covers
 * the whole line.
 */
export function defaultPatchSegment(
  status: Pick<LineStatus, 'accepted_m' | 'direction_deg' | 'start_along_m' | 'end_along_m'>,
  original: PlannedLine,
): Segment | null {
  const length = original.length_m;
  let start: number;
  let forward: boolean;
  if (status.start_along_m != null && status.end_along_m != null) {
    start = status.start_along_m;
    forward = status.end_along_m >= status.start_along_m;
  } else if (status.direction_deg != null) {
    const gap = Math.abs(((status.direction_deg - bearing(original) + 540) % 360) - 180);
    forward = gap <= 90;
    start = forward ? 0 : length;
  } else {
    start = 0;
    forward = true;
  }
  const end = forward ? start + status.accepted_m : start - status.accepted_m;
  const lo = Math.max(0, Math.min(start, end));
  const hi = Math.min(length, Math.max(start, end));
  const pieces: Segment[] = [];
  if (hi <= lo) pieces.push({ from_m: 0, to_m: length });
  else {
    if (lo > 0) pieces.push({ from_m: 0, to_m: lo });
    if (hi < length) pieces.push({ from_m: hi, to_m: length });
  }
  if (pieces.length === 0) return null;
  const big = pieces.filter((p) => p.to_m - p.from_m >= PATCH_MIN_GAP_M);
  const kept =
    big.length > 0 ? big : [pieces.reduce((a, b) => (b.to_m - b.from_m > a.to_m - a.from_m ? b : a))];
  return { from_m: cm(kept[0]!.from_m), to_m: cm(kept[kept.length - 1]!.to_m) };
}

/** Throws a RangeError with a user-facing message when `segment` is not inside `original`. */
export function checkSegment(segment: Segment, original: PlannedLine): void {
  const { from_m, to_m } = segment;
  if (!(Number.isFinite(from_m) && Number.isFinite(to_m)) || from_m < 0 || to_m > original.length_m + 0.005) {
    throw new RangeError(
      `The reflight segment of line ${original.number} must lie between 0 and ${cm(original.length_m)} m.`,
    );
  }
  if (to_m - from_m < 1) {
    throw new RangeError(`The reflight segment of line ${original.number} must end after it starts.`);
  }
}

/** The patch planned line for `segment` of `original`, numbered `number`. */
export function patchLine(
  original: PlannedLine,
  segment: Segment,
  number: number,
  ids: { readonly id: Uuid; readonly now: IsoDateTime },
): PlannedLine {
  checkSegment(segment, original);
  const to = Math.min(segment.to_m, original.length_m);
  const [x1, y1] = pointAlong(original, segment.from_m);
  const [x2, y2] = pointAlong(original, to);
  return {
    ...original,
    id: ids.id,
    created_at: ids.now,
    updated_at: ids.now,
    updated_by: null,
    deleted_at: null,
    number,
    x1: cm(x1),
    y1: cm(y1),
    x2: cm(x2),
    y2: cm(y2),
    length_m: cm(to - segment.from_m),
    reflight_of_line_id: original.id,
    segment_from_m: cm(segment.from_m),
    segment_to_m: cm(to),
  };
}

/** The full reflight planned line of `original`, numbered `number`. */
export function reflightLine(
  original: PlannedLine,
  number: number,
  ids: { readonly id: Uuid; readonly now: IsoDateTime },
): PlannedLine {
  return {
    ...original,
    id: ids.id,
    created_at: ids.now,
    updated_at: ids.now,
    updated_by: null,
    deleted_at: null,
    number,
    reflight_of_line_id: original.id,
    segment_from_m: null,
    segment_to_m: null,
  };
}

/** Planned lines to save and remove with a review, in the same transaction (`line_status_review`). */
export interface PlanChange {
  readonly save: PlannedLine[];
  readonly remove: Uuid[];
}

export const NO_PLAN_CHANGE: PlanChange = { save: [], remove: [] };

export interface FollowUpReview {
  readonly acceptance: Acceptance;
  /** Partial only: the accepted metres the review will store (defaults to the status's flown length). */
  readonly accepted_m?: number;
  /** Partial only: the reviewer's patch segment; the default segment when omitted. */
  readonly segment?: Segment;
}

/**
 * The reflight or patch a review calls for (decision 29). Lines planned after this status (later versions of the
 * same line) that nobody has flown yet are its follow-ups: they are kept, changed or removed so that
 * - rejected → exactly one unflown full reflight (created when missing, a patch replaced by it);
 * - partial → exactly one unflown patch over the segment (created, or an unflown patch moved to the new segment);
 * - accepted or pending → no follow-up (unflown ones are removed).
 * Nothing changes once a later version has been flown (its own review decides from there), when the line has no
 * original in the plan, or when a later version came from an imported lines file (R245: a review neither moves nor
 * removes what someone brought in; take the import back to change it). Throws a RangeError when the line has no version digit left (9) or the segment is invalid.
 */
export function followUpPlan(
  status: LineStatus,
  review: FollowUpReview,
  plan: readonly PlannedLine[],
  statuses: readonly LineStatus[],
  ids: { readonly now: IsoDateTime; readonly newId: () => Uuid },
): PlanChange {
  const parsed = parseLineNumber(status.line_number);
  if (!parsed.production) return NO_PLAN_CHANGE;
  const live = plan.filter((p) => p.deleted_at === null);
  const original = live.find((p) => p.number === parsed.base && plannedLineRole(p) === 'original');
  if (!original) return NO_PLAN_CHANGE;
  const later = live
    .filter((p) => parseLineNumber(p.number).base === parsed.base && p.number > status.line_number)
    .sort((a, b) => a.number - b.number);
  const flown = new Set(
    statuses.filter((s) => s.deleted_at === null).flatMap((s) => [s.planned_line_id, `#${s.line_number}`]),
  );
  if (later.some((p) => flown.has(p.id) || flown.has(`#${p.number}`) || isImportedLine(p)))
    return NO_PLAN_CHANGE;

  const next = (): number => {
    if (parsed.version >= 9) {
      throw new RangeError(`Line ${status.line_number} has no version digit left for another reflight.`);
    }
    return status.line_number + 1;
  };
  const without = (keep: PlannedLine | undefined): Uuid[] =>
    later.filter((p) => p.id !== keep?.id).map((p) => p.id);

  if (review.acceptance === 'rejected') {
    const kept = later.find((p) => plannedLineRole(p) === 'reflight');
    if (kept) return { save: [], remove: without(kept) };
    return {
      save: [reflightLine(original, next(), { id: ids.newId(), now: ids.now })],
      remove: without(undefined),
    };
  }
  if (review.acceptance === 'partial') {
    const accepted_m = review.accepted_m ?? status.flown_m;
    const segment = review.segment ?? defaultPatchSegment({ ...status, accepted_m }, original);
    if (!segment) return { save: [], remove: without(undefined) };
    const kept = later.find((p) => plannedLineRole(p) === 'patch');
    const number = kept ? kept.number : next();
    const patch = patchLine(original, segment, number, {
      id: kept?.id ?? ids.newId(),
      now: ids.now,
    });
    const unchanged =
      kept !== undefined &&
      kept.segment_from_m === patch.segment_from_m &&
      kept.segment_to_m === patch.segment_to_m;
    return {
      save: unchanged ? [] : [kept ? { ...patch, created_at: kept.created_at } : patch],
      remove: without(kept),
    };
  }
  return { save: [], remove: without(undefined) };
}

/** Merge the plan changes of several reviews (later saves of the same line win). */
export function mergePlanChanges(changes: readonly PlanChange[]): PlanChange {
  const save = new Map<Uuid, PlannedLine>();
  const remove = new Set<Uuid>();
  for (const c of changes) {
    for (const l of c.save) save.set(l.id, l);
    for (const id of c.remove) remove.add(id);
  }
  for (const id of save.keys()) remove.delete(id);
  return { save: [...save.values()], remove: [...remove] };
}

/** The plan after a change (for the next review in a batch, and for screens until the plan is reloaded). */
export function applyPlanChange(plan: readonly PlannedLine[], change: PlanChange): PlannedLine[] {
  const gone = new Set(change.remove);
  const saved = new Map(change.save.map((l) => [l.id, l]));
  const out = plan.filter((p) => !gone.has(p.id)).map((p) => saved.get(p.id) ?? p);
  for (const l of change.save) if (!plan.some((p) => p.id === l.id)) out.push(l);
  return out;
}
